// Care host Meta events: Registration Page Visit, Registration, Registration Success.
//
// Each event is sent twice with one shared event id, and Meta keeps one:
//   1. browser: a dataLayer push. GTM-PS4VV6T turns it into a Meta pixel event and passes
//      `event_id` as the pixel's Event ID.
//   2. server: POST /api/meta-event (functions/care careMetaEvent), which sends the same event
//      through Meta's Conversions API, so ad blockers and in-app browsers don't lose it.
//
// Only runs on the care hosts. register.yourdoctors.online keeps its GTM-only tracking unchanged.
// Nothing here throws: tracking must never block sign-up.
//
// The two login events use accountEventId(name, uid), an id tied to the account, because the server
// accepts only that id for them: a repeat of the same account's event then carries the same id and
// Meta keeps one.
//
// Usage:
//   trackCareEvent('RegistrationPageVisit')
//   trackCareEvent('Registration', { uid: user.uid, getIdToken: () => user.getIdToken(), extra: { email } })
//   const id = await accountEventId('RegistrationSuccess', uid); sendServerCopy('RegistrationSuccess', id, getIdToken)
// Test mode: open any care page with ?meta_test=TEST12345. The code is kept for the tab and sent with
// the SERVER copies only, which Meta then keeps out of reporting. The pixel copies still count.
import { newSessionToken } from './places'

export const CARE_HOSTS = new Set([
  'care.yourdoctors.online',
  'ydo-care.web.app',
  'ydo-care.firebaseapp.com',
  'ydo-care-stg.web.app',
  'ydo-care-stg.firebaseapp.com',
])

// dataLayer event names GTM listens for. RegistrationSuccess reuses the existing
// `registrationSuccess` push, which also drives the Google Ads and Reddit sign-up tags.
export const DL_EVENT = {
  RegistrationPageVisit: 'careRegistrationPageVisit',
  Registration: 'careRegistration',
  RegistrationSuccess: 'registrationSuccess',
}

const TEST_KEY = 'ydo_meta_test'
const TEST_CODE = /^TEST[A-Z0-9]{1,20}$/

/** @returns {boolean} true when the page is served from a care host. */
export function isCareHost(hostname = window.location.hostname) {
  return CARE_HOSTS.has(hostname)
}

/** @returns {string} a random UUID used as the shared browser/server event id. */
export const newEventId = newSessionToken

/** @returns {string|null} the named cookie's value (used for Meta's _fbp and _fbc). */
export function readCookie(name) {
  try {
    const hit = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`))
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null
  } catch (e) {
    return null
  }
}

const LANDING_KEY = 'ydo_landing_params'

/**
 * Keeps the tab's first set of landing-link parameters (utm_*, fbclid, gclid...) on the care hosts,
 * so they survive a reload before sign-up. First set wins: a later link in the same tab doesn't
 * overwrite it. Does nothing off care. Storage failures are ignored.
 * @param {object|null} params the link parameters routes.js collected (or null)
 */
export function rememberLandingParams(params) {
  try {
    if (!isCareHost() || !params || !Object.keys(params).length) return
    if (window.sessionStorage.getItem(LANDING_KEY)) return
    window.sessionStorage.setItem(LANDING_KEY, JSON.stringify(params))
  } catch (e) {
    // storage blocked (private mode / in-app browser): the parameters stay in memory only
  }
}

/** @returns {object|null} the tab's stored landing parameters on care, or null. */
export function storedLandingParams() {
  try {
    if (!isCareHost()) return null
    const raw = window.sessionStorage.getItem(LANDING_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

/** @returns {string|null} the Meta test code from ?meta_test= or earlier in this tab. */
export function testEventCode() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('meta_test')
    if (fromUrl && TEST_CODE.test(fromUrl)) {
      window.sessionStorage.setItem(TEST_KEY, fromUrl)
      return fromUrl
    }
    const kept = window.sessionStorage.getItem(TEST_KEY)
    return kept && TEST_CODE.test(kept) ? kept : null
  } catch (e) {
    return null
  }
}

/**
 * Sends the server copy of one event to /api/meta-event. Resolves to the relay's JSON answer, or
 * null on any failure. keepalive lets the request finish when the page navigates away.
 * @param {string} eventName RegistrationPageVisit | Registration | RegistrationSuccess
 * @param {string} eventId the id already pushed to the dataLayer
 * @param {function} [getIdToken] resolves to the Firebase ID token (required by the two login events)
 */
export async function sendServerCopy(eventName, eventId, getIdToken) {
  try {
    const headers = { 'Content-Type': 'application/json' }
    if (getIdToken) headers.Authorization = `Bearer ${await getIdToken()}`
    const body = {
      eventName,
      eventId,
      url: window.location.href,
      fbp: readCookie('_fbp'),
      fbc: readCookie('_fbc'),
    }
    const code = testEventCode()
    if (code) body.testEventCode = code
    // The ad click as it arrived; the server keeps it in the account's sent note (not sent to Meta).
    const landing = storedLandingParams()
    if (landing) body.landing = landing
    const res = await fetch('/api/meta-event', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    })
    return await res.json()
  } catch (e) {
    return null
  }
}

const ID_PREFIX = { Registration: 'reg', RegistrationSuccess: 'regok' }

/**
 * The event id for a login event, tied to the account: "<prefix>-<first 32 hex of
 * sha256('<eventName>:<uid>')>". Must match accountEventId in functions/care/metaEvent.js.
 * @param {string} eventName Registration | RegistrationSuccess
 * @param {string} uid Firebase uid
 * @returns {Promise<string|null>} the id, or null when the browser has no Web Crypto (then the
 *   caller sends the pixel copy without an id and skips the server copy, which would be refused)
 */
export async function accountEventId(eventName, uid) {
  try {
    const bytes = new TextEncoder().encode(`${eventName}:${uid}`)
    const digest = await window.crypto.subtle.digest('SHA-256', bytes)
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    return `${ID_PREFIX[eventName]}-${hex.slice(0, 32)}`
  } catch (e) {
    return null
  }
}

/**
 * Sends one care event to the pixel (via the dataLayer) and to the server relay.
 * @param {string} eventName RegistrationPageVisit | Registration
 * @param {object} [opts] { uid, getIdToken, extra } uid is required for Registration (its id is
 *   tied to the account); extra fields go on the dataLayer push (e.g. email for advanced matching)
 * @returns {Promise<string|null>} the event id, or null when not on a care host or no id was made.
 */
export async function trackCareEvent(
  eventName,
  { uid, getIdToken, extra } = {},
) {
  // Callers fire and forget, so nothing may reject: any surprise resolves to null.
  try {
    if (!isCareHost()) return null
    const eventId = uid ? await accountEventId(eventName, uid) : newEventId()
    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: DL_EVENT[eventName],
        ...(eventId ? { event_id: eventId } : {}),
        ...extra,
      })
    } catch (e) {
      // GTM missing or blocked: the server copy still goes out
    }
    if (eventId) sendServerCopy(eventName, eventId, getIdToken)
    return eventId
  } catch (e) {
    return null
  }
}

// The sign-up screen can mount more than once per page load (StrictMode, back navigation);
// Registration Page Visit counts page loads, like register's GTM rule does.
let pageVisitSent = false
/** Sends RegistrationPageVisit once per page load on a care host. @returns {Promise<string|null>|null} */
export function trackCarePageVisitOnce() {
  if (pageVisitSent || !isCareHost()) return null
  pageVisitSent = true
  return trackCareEvent('RegistrationPageVisit')
}
